import { useAuth } from "context/AuthContext"
import React, { useEffect, useState } from "react"
import { useMediaQuery } from "react-responsive"
import { toast } from "react-toastify"
import { getOrCreateConversation } from "services/firestore/getOrCreateConversation"
import styled from "styled-components"
import { ButtonPrimary } from "ui/base/Button/ButtonStyled"
import { ClassInfoCard } from "ui/base/class/components/ClassInfoCard"
import { LoadingSpinner } from "ui/base/loading/LoadingSpinner"
import { Modal } from "ui/base/modal/Modal"
import { TextArea } from "ui/base/textArea/TextArea"
import { AlertCircle } from "ui/icons/AlertCircle"
import { defaultThemeColors } from "ui/theme"
import { updateTypingStatus } from "../../../../services/database/updateTypingStatus"
import { sendMessage } from "../../../../services/firestore/sendMessage"

export const SendMessageModal = ({ classDetail, onSignupModal }) => {
  const { userData, userRole, token } = useAuth()
  const [show, setShow] = useState(false)
  const [text, setText] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const [conversationId, setConversationId] = useState(null)
  const [isMsgSent, setIsMsgSent] = useState(!false)
  const teacherInfo = classDetail?.teacher

  const creatorId = userData?.id?.toString()
  const creatorRole = userRole
  const creatorName = `${userData?.first_name} ${
    userData?.last_name.split("")[0]
  }`
  const creatorProfilePicture =
    userRole === 1 ? userData?.profile_pic : userData?.teacher?.profile_pic

  const participantId = classDetail?.id?.toString()
  const receiverUserName = `${classDetail?.first_name} ${
    classDetail?.last_name?.split("")[0]
  }`
  const participantProfilePicture = teacherInfo?.profile_pic
  const participantRole = userRole === 1 ? 2 : 1

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" })

  useEffect(() => {
    if (!classDetail && !creatorId && !participantId) return

    const fetchConversation = async () => {
      const id = await getOrCreateConversation(
        creatorId,
        creatorName,
        creatorProfilePicture,
        creatorRole,
        participantId,
        receiverUserName,
        participantProfilePicture,
        participantRole
      )
      setConversationId(id)
    }

    fetchConversation()
  }, [classDetail, creatorId, participantId])

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!text.trim()) return

    setIsLoading(true)

    try {
      if (!participantId) {
        console.error("Receiver ID not found")
        return
      }

      updateTypingStatus(conversationId, creatorId, false)

      await sendMessage(
        text,
        conversationId,
        creatorId,
        participantId,
        receiverUserName
      )

      setText("")

      setIsMsgSent(true)
    } catch (error) {
      console.error("Error sending message:", error)
      toast.error("An error occured while sending a message.")
    } finally {
      setIsLoading(false)
    }
  }

  const toggleInstructionsModal = () => {
    if (!token) {
      onSignupModal(true)
      return
    }
    setIsMsgSent(false)
    setShow((prev) => !prev)
  }

  return (
    <>
      <SendMessageBtn onClick={toggleInstructionsModal}>
        Send a message
      </SendMessageBtn>
      <Modal
        show={show}
        handleClose={() => setShow(false)}
        maxWidth={isMsgSent ? 500 : 900}>
        <ModalBody>
          {!isMsgSent ? (
            <>
              <LeftPart isMobile={isMobile}>
                <ClassInfoCard
                  classInfo={{
                    first_name: classDetail?.first_name,
                    profile_pic: teacherInfo?.profile_pic,
                    class: teacherInfo?.subject_sub_category,
                    rating: teacherInfo?.rate,
                    class_type: teacherInfo?.type_of_class,
                    review: teacherInfo?.review,
                    online_type: teacherInfo?.online_type,
                  }}
                />
              </LeftPart>
              <RightPart isMobile={isMobile}>
                <div>
                  <TextArea
                    label={
                      !isMobile ? "Write your message to the instructor" : ""
                    }
                    placeholder='Type message...'
                    style={{ height: 338 }}
                    value={text}
                    setValue={(text) => {
                      setText(text)
                    }}
                  />
                </div>
                <ButtonContainer>
                  <ButtonPrimary
                    style={{
                      width: "100%",
                    }}
                    disabled={!text || isLoading}
                    onClick={handleSendMessage}>
                    {isLoading ? (
                      <>
                        <LoadingSpinner variant='white' />
                        <span
                          style={{
                            color: "#d9d9d9",
                          }}>
                          Loading...
                        </span>
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </ButtonPrimary>
                </ButtonContainer>
              </RightPart>
            </>
          ) : (
            <ThanksContent>
              <AlertWrap>
                <AlertCircle />
              </AlertWrap>
              <Title>Message Sent!</Title>
              <Message>
                When the instructor replies, you will see it on the messages
                page.
              </Message>
              <FullButtonPrimary onClick={() => setShow(false)}>
                Done
              </FullButtonPrimary>
            </ThanksContent>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

const SendMessageBtn = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
  background: transparent;
  border: 2px solid #1d1d1f;
  border-radius: 100px;
  padding: 12px 24px;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    border-color: #1d1d1f;
    background: rgba(29, 29, 31, 0.04);
  }
`

const ModalBody = styled.div`
  display: flex;
  padding-block: 1rem;
  padding-inline: 1.5rem;
  gap: 2rem;
`

const LeftPart = styled.div`
  width: 40%;
  display: ${({ isMobile }) => (isMobile ? "none" : "block")};

  & > img {
    object-fit: cover;
    max-width: 280px;
    width: 100%;
    aspect-ratio: 28 / 17;
    border-radius: 5px;
    border-width: 1px;
    border-style: solid;
    border-color: ${defaultThemeColors.lightBlue};
  }
`

const RightPart = styled.div`
  width: ${({ isMobile }) => (isMobile ? "100%" : "60%")};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ThanksContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  /* margin-bottom: 3rem; */
`

const AlertWrap = styled.div`
  margin-bottom: 12px;
  padding: 20px;
  width: 45px;
  height: 45px;
  box-sizing: content-box;
  border-radius: 50%;
  background-color: #edf0ff;
`

const Title = styled.h2`
  font-weight: 600;
  font-size: 26px;
  letter-spacing: 0%;
  text-align: center;
`

const Message = styled.p`
  font-weight: 400;
  font-size: 18px;
  line-height: 155%;
  letter-spacing: 0%;
  text-align: center;
`

const FullButtonPrimary = styled(ButtonPrimary)`
  width: calc(100% - 60px);
  margin-top: 20px;
`

const ButtonContainer = styled.div`
  display: flex;
`
